import axios from "axios";

export const APP_BASE_URL = process.env.REACT_APP_APP_BASE_URL;

const CancelToken = axios.CancelToken;
export const source = CancelToken.source();

const endpoint = axios.create({
  baseURL: APP_BASE_URL,
});

const keyStr = process.env.REACT_APP_ENC_KEY;

// Helper: convert hex string to Uint8Array
function hexToBytes(hex) {
  var bytes = new Uint8Array(hex.length / 2);
  for (var i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return bytes;
}

// Helper: convert Uint8Array to hex string
function bytesToHex(bytes) {
  return Array.from(bytes)
    .map(function (b) {
      return b.toString(16).padStart(2, "0");
    })
    .join("");
}

// Import raw key string into CryptoKey
async function importKey(keyStr) {
  var keyBytes = new TextEncoder().encode(keyStr);
  return crypto.subtle.importKey("raw", keyBytes, "AES-GCM", false, [
    "encrypt",
    "decrypt",
  ]);
}

// Encrypt text (or object) using AES-256-GCM
export async function encryptAsync(text) {
  var cryptoKey = await importKey(keyStr);

  if (typeof text === "object" && text !== null) {
    text = JSON.stringify(text);
  } else if (typeof text !== "string") {
    text = String(text);
  }

  var encoder = new TextEncoder();
  var data = encoder.encode(text);

  var iv = crypto.getRandomValues(new Uint8Array(12)); // 12-byte IV

  var encryptedBuffer = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv },
    cryptoKey,
    data,
  );

  var encryptedArray = new Uint8Array(encryptedBuffer);

  // Split ciphertext and authTag (last 16 bytes)
  var authTag = encryptedArray.slice(-16);
  var ciphertext = encryptedArray.slice(0, -16);

  // Return in format: iv:ciphertext:authTag (all hex)
  return (
    bytesToHex(iv) + ":" + bytesToHex(ciphertext) + ":" + bytesToHex(authTag)
  );
}

// Decrypt text using AES-256-GCM
export async function decryptAsync(text) {
  var textParts = text.split(":");
  if (textParts.length !== 3) {
    throw new Error("Invalid encryption data format");
  }

  var iv = hexToBytes(textParts[0]);
  var ciphertext = hexToBytes(textParts[1]);
  var authTag = hexToBytes(textParts[2]);

  var cipherWithTag = new Uint8Array(ciphertext.length + authTag.length);
  cipherWithTag.set(ciphertext);
  cipherWithTag.set(authTag, ciphertext.length);

  var cryptoKey = await importKey(keyStr);

  var decryptedBuffer = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: iv },
    cryptoKey,
    cipherWithTag,
  );

  var decoder = new TextDecoder();
  var decryptedText = decoder.decode(decryptedBuffer);

  try {
    var parsedValue = JSON.parse(decryptedText);
    if (typeof parsedValue === "object" && parsedValue !== null) {
      decryptedText = parsedValue;
    }
    return decryptedText;
  } catch (e) {
    return decryptedText;
  }
}

export default endpoint;
